import { UserType } from '../types';
import { BasicToastOptions, ToastPosition, ToastPurpose } from './types';

export const DEFAULT_POSITION = 'bottom-center' as const satisfies ToastPosition;

export const DEFAULT_BASIC_OPTIONS = {
  isDismissible: true,
  isMasked: false,
  position: DEFAULT_POSITION,
  timeout: 10000,
} as const satisfies BasicToastOptions;

/**
 * Mapping from contextual toast purpose to the position intended for that type
 * of information.
 */
export const PURPOSE_TO_POSITION: Record<ToastPurpose, ToastPosition> = {
  system: 'top',
  session: 'bottom-center',
  prompt: 'bottom-right',
  realtime: 'bottom-left',
};

/**
 * Mapping from toast positions to class names which should be applied to
 * "basic" toasts (i.e. success, error, info) to size them consistently.
 */
export const POSITION_TO_TOAST_CLASSNAMES: Record<ToastPosition, string> = {
  top: 'sm:w-max sm:min-w-[33rem] sm:max-w-[38rem] md:max-w-[52rem]',
  'bottom-left': 'sm:w-md',
  'bottom-center': 'sm:w-md',
  'bottom-right': 'sm:w-md',
};

/**
 * Mapping from toast positions to class names which should be applied to
 * properly position that region of toasts.
 */
export const POSITION_TO_REGION_CLASSNAMES: Record<ToastPosition, string> = {
  top: 'top-0 left-1/2 transform -translate-x-1/2',
  'bottom-left': 'left-0',
  'bottom-center': 'left-1/2 transform -translate-x-1/2',
  'bottom-right': 'right-0',
};

/**
 * Mapping from toast positions to a function which takes a user type as an
 * argument and returns a contextual string which should be used for the ARIA
 * label for that region of toasts.
 */
export const POSITION_TO_REGION_LABEL: Record<ToastPosition, (userType: UserType) => string> = {
  top: () => 'System notifications',
  'bottom-left': (userType) => `${userType === UserType.PROVIDER ? 'Client and chat ' : 'Chat'} updates`,
  'bottom-center': () => 'Session notifications',
  'bottom-right': () => 'Session prompts',
};
